'use client';

import { Portal } from '@radix-ui/react-tooltip';
import { ColumnDef } from '@tanstack/react-table';
import { formatDate } from 'date-fns';
import Link from 'next/link';

import React, { ForwardedRef, MutableRefObject, memo, useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuRadioItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';

import { markdownToComponent } from '@/utils/convertMarkdown';
import advanceFlow from '@/utils/flows/advanceFlow';
import formatName from '@/utils/format/formatName';
import formatNumber from '@/utils/format/formatNumber';
import formatURL, { formatURLWithPath } from '@/utils/format/formatURL';
import getCurrentIds from '@/utils/getCurrentIds';
import getIcon from '@/utils/getIcon';
import { CheckSquare } from '@/utils/lucide';
import { Square } from '@/utils/lucide';
import { Filter } from '@/utils/lucide';
import { Info } from '@/utils/lucide';
import { FileCode } from '@/utils/lucide';
//Icons and icon key
import { ChevronDown } from '@/utils/lucide';
import { ChevronUp } from '@/utils/lucide';
import { ExternalLink } from '@/utils/lucide';
import { ChevronsDownUp, ChevronsUpDown } from '@/utils/lucide';
import openDetail from '@/utils/openDetail';
import truncateMarkdown from '@/utils/truncateMarkdown';

import { addFieldState, setFieldState } from '@/redux/interactionSlice';
import store, { RootState } from '@/redux/store';

import { ColumnType, Row } from '@/types/DataGrid';

import OpenableTitle from '../Layout/OpenableTitle';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import ListCell from './ListCell';
import ResearchCell from './ResearchCell';
import SelectAllRowsButton from './SelectAllRowsButton';
import TextCell from './TextCell';

interface BaseCellProps
{
	row: any;
	column: any;
	cell: any;
	table?: any;
}
interface ListCellProps extends BaseCellProps
{
	value: any[];
}
interface TextCellProps extends BaseCellProps
{
	value: string | { value: string };
}
interface TextCellOptions
{
	truncate?: number;
	truncateOffset?: number;
	md: boolean;
}
interface Evidence
{
	value: any;
	evidence_rating?: string;
	id: string;
	status?: string;
}

function getColumn(col: ColumnType, identifier?: string)
{
	//@ts-ignore -- type error while between backend commits
	const colType = col.data_type;

	let cell;
	let column: ColumnDef<Row> = {
		header: ({ table, column }: any) =>
		{
			let isTitle = colType === 'title';
			let isResearch = !!column.columnDef.meta.research;
			let sortable = !!col.sortable;

			const selected = table.getIsAllRowsSelected();
			// const isSorted =
			// 	table.options.state.sorting && table.options.state.sorting.length > 0 && table.options.state.sorting[0].id === column.columnDef.accessorKey
			// 		? table.options.state.sorting[0].desc
			// 		: false; //column.getIsSorted();

			const sortingState = table.options.state.sorting;
			const sortStateEntry = sortingState.find((s: any) => s.id === column.columnDef.accessorKey);
			const currentSortDirection = sortStateEntry ? (sortStateEntry.desc ? 'desc' : 'asc') : false;

			return (
				<div className='flex flex-row items-center w-full @container/header gap-1'>
					<Tooltip delayDuration={0}>
						<TooltipTrigger asChild>
							<div className='flex flex-row items-center gap-0.5'>
								{isTitle ? (
									<SelectAllRowsButton onClick={table.getToggleAllRowsSelectedHandler()} column={column} selected={selected} table={table} />
								) : (
									<p className=''>{col.display_name ?? formatName(col.key)}</p>
								)}
								<Button variant='ghost' className='px-0.5 w-full text-left justify-start text-base hidden'>
									<Info className=' h-4 w-4' />
								</Button>
							</div>
						</TooltipTrigger>
						{(column.columnDef.meta?.description_key || col.description) && (
							<Portal>
								<TooltipContent className='flex flex-col bg-white p-4'>
									<p>
										{col.instructions && 'Description: '}
										{col.description ??
											store.getState().enums.enums?.col_descriptions?.[column.columnDef.meta?.description_key] ??
											'No description available'}
									</p>
									{col.instructions && <p className='pt-1'>Instructions: {col.instructions}</p>}
								</TooltipContent>
							</Portal>
						)}
					</Tooltip>

					<div className='hidden @[150px]/header:flex flex-row items-center  '>
						{sortable && (
							<button
								onClick={() =>
								{
									console.log('Toggling sort', column.columnDef.accessorKey, column.getIsSorted(), table);

									//false (unsorted) -> asc -> desc -> false (unsorted)
									let nextSortDirection: boolean | 'asc' | 'desc' = false;
									if (currentSortDirection === false)
									{
										nextSortDirection = 'asc';
									}
									else if (currentSortDirection === 'asc')
									{
										nextSortDirection = 'desc';
									}

									console.log('Toggling sort', sortingState, currentSortDirection, nextSortDirection);

									if (nextSortDirection)
									{
										//table.setSorting([{ id: column.columnDef.accessorKey, desc: nextSortDirection }]);
										column.toggleSorting(nextSortDirection === 'desc');
									}
									else
									{
										column.clearSorting();
									}
								}}
								className='cursor-pointer flex flex-row items-center px-0.5 text-left justify-start text-base'
							>
								{currentSortDirection === false && <ChevronsUpDown className='h-4 min-w-4' />}
								{currentSortDirection === 'asc' && <ChevronDown className='h-4 min-w-4' />}
								{currentSortDirection === 'desc' && <ChevronUp className='h-4 min-w-4' />}
							</button>
						)}

						<Button
							variant='ghost'
							onClick={() =>
							{
								console.log('Setting filter trigger', column.columnDef.accessorKey, identifier);

								store.dispatch(
									addFieldState({
										key: 'filter-trigger',
										value: {
											column,
											identifier
										}
									})
								);
							}}
							className='px-0.5 w-full text-left justify-start text-base'
						>
							<Filter className='h-4 w-4' />
						</Button>
					</div>
				</div>
			);
		},

		accessorKey: col.key,
		enableResizing: true,
		minSize: 150,
		maxSize: 500,
		meta: {
			sizeWeight: 1,
			noSelect: col.noSelect,
			research: col.research,
			displayName: col.display_name,
			description_key: col.description_key,
			raw: col
		}
	};

	switch (colType)
	{
		case 'md':
		case 'md_string':
			cell = ({ table, row, column, cell }: any) =>
			{
				const truncate = col.truncate ?? 250;

				//console.log('Cell', cell, cell.getValue(), truncate);

				return <TextCell value={cell.getValue()} row={row} column={column} cell={cell} md={true} truncate={truncate} table={table} />;
			};
			break;
		case 'title':
			cell = ({ table, row, column, cell }: any) =>
			{
				const selected = row.getIsSelected();

				let title, id, type;
				let value = cell.getValue();

				if (value === null || value === undefined)
				{
					value = 'No Title Available';
				}
				else if (Array.isArray(value)) value = value[0];

				if (Object.hasOwnProperty.call(value, 'name') || Object.hasOwn(value, 'id'))
				{
					title = value.name;
					id = value.id;
					type = value.type;
				}
				else
				{
					title = value;
					id = row.original.id;
					type = table.options.entity_type;
				}

				//console.log('Title cell', title, id, type, row, row.original);

				if (type === 'custom' || type === 'other')
				{
					return (
						<div
							className='flex flex-row items-center gap-2'
							onDoubleClick={() =>
							{
								row.toggleSelected();
							}} // on double click, toggle selection
						>
							<button
								id={`row-select-${id}`}
								onClick={e =>
								{
									//console.log('Toggling select', row, row.id);
									row.toggleSelected();
									e.stopPropagation();
									e.preventDefault();
								}}
							>
								<p>{selected ? <CheckSquare className='text-icon' /> : <Square className='text-icon' />}</p>
							</button>
							{markdownToComponent(title, {
								spacing: false
							})}
						</div>
					);
				}
				else
				{
					return (
						<div
							onDoubleClick={() =>
							{
								row.toggleSelected();
							}} // on double click, toggle selection
						>
							<OpenableTitle
								title={title}
								id={id}
								type={type}
								className='font-bold'
								pre={
									<button
										id={`row-select-${id}`}
										onClick={e =>
										{
											//console.log('Toggling select', row, row.id);
											row.toggleSelected();
											e.stopPropagation();
											e.preventDefault();

											//If part of guided flow, advance to next step
											advanceFlow('custom_table_builder_new_list', 'select_items');
										}}
									>
										<p>{selected ? <CheckSquare className='text-icon' /> : <Square className='text-icon' />}</p>
									</button>
								}
							/>
						</div>
					);
				}
			};
			column.meta = {
				...column.meta,
				sizeWeight: 1.5
			};
			column.minSize = 150;

			column.sortingFn = 'alphanumeric';
			column.filterFn = 'includesString';
			break;
		case 'str':
		case 'short':
			cell = ({ table, row, column, cell }: any) =>
			{
				if (!cell.getValue())
				{
					return (
						<div>
							<p className='text-sm text-neutral-400'>No Value Available</p>
						</div>
					);
				}

				if (typeof cell.getValue() !== 'string')
				{
					return <div>{JSON.stringify(cell.getValue())}</div>;
				}

				if (cell.getValue().toLowerCase().includes('http'))
				{
					return (
						<Link href={cell.getValue()} className='underline text-blue-400 hover:text-blue-700 visited:text-purple-600' target='_blank'>
							{formatURLWithPath(cell.getValue())}
						</Link>
					);
				}

				return <div>{formatName(cell.getValue())}</div>;
			};
			column.sortingFn = 'alphanumeric';
			break;

		case 'entity_type':
			cell = ({ table, row, column, cell }: any) =>
			{
				if (!cell.getValue())
				{
					return (
						<div>
							<p className='text-sm text-neutral-400'>No Value Available</p>
						</div>
					);
				}

				const entity = cell.getValue();
				const Icon = getIcon(entity);

				return (
					<div className='flex flex-row items-center gap-x-2'>
						<Icon className='text-icon' />
						<p>{formatName(entity)}</p>
					</div>
				);
			};
			column.sortingFn = 'alphanumeric';
			break;
		case 'companies':
			cell = ({ table, row, column, cell }: any) =>
			{
				//If null or missing, show as 'No Value Available'
				if (!cell.getValue() || cell.getValue().length === 0)
				{
					return (
						<div>
							<p className='text-sm text-neutral-400'>No Value Available</p>
						</div>
					);
				}

				//If it's properly an array, show as a li
				if (Array.isArray(cell.getValue()))
				{
					return <ListCell value={cell.getValue()} row={row} column={column} cell={cell} table={table} />;
				}
				else
				{
					let array = JSON.parse(cell.getValue());
					if (!Array.isArray(array))
					{
						return <div>{cell.getValue()}</div>;
					}
					return <ListCell value={array} row={row} column={column} cell={cell} table={table} />;
				}
			};

			//2x
			column.meta = {
				...column.meta,
				sizeWeight: 2
			};
			column.minSize = 200;
			column.maxSize = 800;

			break;
		case 'long':
			cell = ({ table, row, column, cell }: any) =>
			{
				const truncate = col.truncate ?? 250;
				return <TextCell value={cell.getValue()} row={row} column={column} cell={cell} md={false} truncate={truncate} table={table} />;
			};
			column.meta = {
				...column.meta,
				sizeWeight: 3
			};
			column.minSize = 200;
			break;
		case 'compound':
		case 'list':
			cell = ({ table, row, column, cell }: any) =>
			{
				//If null or missing, show as 'No Value Available'
				if (!cell.getValue() || cell.getValue().length === 0)
				{
					return (
						<div>
							<p className='text-sm text-neutral-400'>No Value Available</p>
						</div>
					);
				}

				//If it's properly an array, show as a li
				if (Array.isArray(cell.getValue()))
				{
					return <ListCell value={cell.getValue()} row={row} column={column} cell={cell} table={table} />;
				}
				else
				{
					let displayString = '';
					if (typeof cell.getValue() === 'string' || typeof cell.getValue() === 'number')
					{
						displayString = cell.getValue();
					}
					else
					{
						displayString = 'No Value Available';
					}

					return <div>{displayString}</div>;
				}
			};

			column.meta = {
				...column.meta,
				sizeWeight: 1.5
			};
			column.minSize = 150;
			break;
		case 'currency':
			cell = ({ table, row, column, cell }: any) =>
			{
				if (!cell.getValue())
				{
					return (
						<div>
							<p className='text-sm text-neutral-400'>No Value Available</p>
						</div>
					);
				}

				return <div>${formatNumber(cell.getValue())}</div>;
			};
			break;
		case 'int':
		case 'number':
			cell = ({ table, row, column, cell }: any) =>
			{
				if (!cell.getValue())
				{
					return (
						<div>
							<p className='text-sm text-neutral-400'>No Value Available</p>
						</div>
					);
				}

				let number = cell.getValue();
				if (typeof number !== 'number')
				{
					return <div>{JSON.stringify(number)}</div>;
				}
				if (number < 50000)
				{
					return <div>{`${number}`}</div>;
				}

				return <div>${formatNumber(cell.getValue())}</div>;
			};
			column.meta = {
				...column.meta,
				sizeWeight: 0.5
			};
			break;
		case 'date':
			cell = ({ table, row, column, cell }: any) =>
			{
				if (!cell.getValue())
				{
					return (
						<div>
							<p className='text-sm text-neutral-400'>No Value Available</p>
						</div>
					);
				}

				let dateString = cell.getValue();
				try
				{
					//const dateFormat = 'MM/dd/yyyy';
					const dateFormat = 'MMMM d, yyyy';
					dateString = formatDate(cell.getValue(), dateFormat);
				}
				catch (e)
				{
					console.log('Error formatting date', e);
				}

				return <div>{dateString}</div>;
			};
			column.sortingFn = 'datetime';
			break;
		case 'link':
			cell = ({ table, row, column, cell }: any) =>
			{
				const value = cell.getValue();
				if (!value || typeof value !== 'string' || !value.startsWith('http'))
				{
					return (
						<div>
							<p>No Link Available</p>
						</div>
					);
				}

				return (
					<Link href={cell.getValue()} className='underline text-blue-400 hover:text-blue-700 visited:text-purple-600' target='_blank'>
						{cell.getValue()}
					</Link>
				);
			};
			break;
		case 'view':
			cell = ({ table, row, column, cell }: any) =>
			{
				//console.log('cell', cell, cell.getValue(), 'row', row, 'column', column, 'table', table);
				return (
					<div className='flex flex-col items-center justify-center w-full'>
						<Button variant={'primary'} className='w-full'>
							View
						</Button>
					</div>
				);
			};
			column.enableResizing = false;
			break;
		case 'open':
		case 'open_detail':
			cell = ({ table, row, column, cell }: any) =>
			{
				const onClick = (e: any) =>
				{
					console.log(table);
					const entity_type = table.options.entity_type;
					if (!entity_type)
					{
						console.error('No entity type specified for table');
						return;
					}

					const id = row.original.id;
					if (!id)
					{
						console.error('No id found for row', row.original);
						return;
					}

					console.log('Navigating to entity', entity_type, id);
					const { session_id } = getCurrentIds();
					socket.emit('take_action', {
						action_type: `${entity_type}_detail`,
						id,
						session_id,
						tab_id: null
					});

					e.stopPropagation();
					e.preventDefault();
				};

				//console.log('cell', cell, cell.getValue(), 'row', row, 'column', column, 'table', table);
				return (
					<div className='flex flex-col items-center justify-center w-full'>
						<Button variant={'primary'} className='flex flex-row items-center w-full gap-x-2' onClick={onClick}>
							<p>Open Detail</p>
							<p className='text-icon-sm'>
								<ExternalLink />
							</p>
						</Button>
					</div>
				);
			};

			break;
		case 'research':
			cell = ({ table, row, column, cell }: any) =>
			{
				return <ResearchCell cellProps={{ table, row, column, cell }} colType={col} />;
			};

			column.meta = {
				...column.meta,
				sizeWeight: 3
			};
			column.minSize = 200;
			break;
		case 'passthru':
			cell = col.cell;
			break;
		default:
			cell = ({ table, row, column, cell }: any) =>
			{
				if (!cell.getValue())
				{
					return (
						<div>
							<p className='text-sm text-neutral-400'>No Value Available</p>
						</div>
					);
				}
				return <div>{JSON.stringify(cell.getValue())}</div>;
			};
			break;
	}

	//If any of weight, min_size, or max_size are provided, set them.
	if (col.min_size)
	{
		column.minSize = col.min_size;
	}
	if (col.max_size)
	{
		column.maxSize = col.max_size;
	}
	if (col.weight)
	{
		column.meta = {
			...column.meta,
			sizeWeight: col.weight
		};
	}

	//If the max size is small, than increase
	const weight = (column.meta as any).sizeWeight;
	column.maxSize = Math.max(column.maxSize ?? 200, 100 * weight);

	//If this column has a specific order, set the sorting function to use that order
	if (col.order)
	{
		column.sortingFn = (a, b, dir) =>
		{
			const order = col.order!;
			const aIndex = order.indexOf(a.original[col.key]);
			const bIndex = order.indexOf(b.original[col.key]);

			//console.log('Sorting in', order, a, b, dir, aIndex, bIndex);

			if (aIndex === -1 && bIndex === -1)
			{
				return 0;
			}
			if (aIndex === -1)
			{
				return 1;
			}
			if (bIndex === -1)
			{
				return -1;
			}
			return aIndex - bIndex;
		};
	}

	column.cell = cell;

	//console.log('Column', column, 'generated from', col);

	return column;
}

function getColumnDef(cols: ColumnType[], identifier?: string)
{
	if (!cols || !Array.isArray(cols))
	{
		console.log('No columns provided to getColumnDef', cols);
		return [];
	}

	const columns: ColumnDef<Row>[] = [];
	cols.forEach(col =>
	{
		columns.push(getColumn(col, identifier));
	});

	return columns;
}

export { getColumn, getColumnDef };
export type { BaseCellProps, ListCellProps, TextCellProps, TextCellOptions, Evidence };
